import React, { useState, useEffect } from 'react';

const API = "https://surabaya-api.onrender.com/api";

const MOIS_NOMS = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function ym(year, month) {
  return year + "-" + String(month).padStart(2, "0");
}

function getPreviousMonth() {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return ym(d.getFullYear(), d.getMonth() + 1);
}

function formatLabel(s) {
  if (!s) return "";
  const [y, m] = s.split("-");
  return MOIS_NOMS[parseInt(m, 10)] + " " + y;
}

function clamp(value, min, max) {
  if (min && value < min) return min;
  if (max && value > max) return max;
  return value;
}

export default function BilanCompta() {
  const prev = getPreviousMonth();
  const [from, setFrom] = useState(prev);
  const [to, setTo] = useState(prev);
  const [bounds, setBounds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);

  useEffect(() => {
    fetch(API + "/bilan/synthese")
      .then(r => r.json())
      .then(d => {
        if (d && d.periode && d.periode.from && d.periode.to) {
          setBounds({ from: d.periode.from, to: d.periode.to });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const valid = from && to && from <= to;

  const annees = [];
  if (bounds) {
    const yMin = parseInt(bounds.from.split("-")[0], 10);
    const yMax = parseInt(bounds.to.split("-")[0], 10);
    for (let y = yMin; y <= yMax; y++) annees.push(y);
  }

  const setMoisPrecedent = () => {
    const m = getPreviousMonth();
    setFrom(m); setTo(m);
  };
  const setAnnee = (year) => {
    let f = ym(year, 1), t = ym(year, 12);
    if (bounds) {
      f = clamp(f, bounds.from, bounds.to);
      t = clamp(t, bounds.from, bounds.to);
    }
    setFrom(f); setTo(t);
  };
  const setTout = () => {
    if (bounds) { setFrom(bounds.from); setTo(bounds.to); }
  };

  const handleImport = async () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    setImporting(true);
    setImportMsg(null);
    try {
      const res = await fetch(API + "/bilan/import-z?year=" + y + "&month=" + m);
      const data = await res.json();
      if (data.success) {
        setImportMsg({ type: "ok", text: "Import OK : " + data.ventes + " ventes / " + data.ttc.toFixed(2) + " EUR TTC pour " + MOIS_NOMS[m] + " " + y });
        // Refresh bornes
        const r2 = await fetch(API + "/bilan/synthese");
        const d2 = await r2.json();
        if (d2 && d2.periode) setBounds({ from: d2.periode.from, to: d2.periode.to });
      } else {
        setImportMsg({ type: "err", text: "Echec : " + (data.error || "erreur inconnue") });
      }
    } catch (e) {
      setImportMsg({ type: "err", text: "Erreur reseau : " + e.message });
    } finally {
      setImporting(false);
    }
  };

  const pdfUrl = API + "/bilan/synthese/pdf?from=" + from + "&to=" + to;
  const xlsxUrl = API + "/bilan/synthese/xlsx?from=" + from + "&to=" + to;

  const periodeLabel = valid
    ? (from === to
        ? formatLabel(from)
        : formatLabel(from) + " — " + formatLabel(to))
    : "Période invalide";

  const horsBornes = bounds && (from < bounds.from || to > bounds.to);

  const inputStyle = {
    width: "100%", padding: "8px 12px", fontSize: 14,
    border: "0.5px solid var(--color-border-tertiary)", borderRadius: 6,
    background: "var(--color-background-primary)", color: "var(--color-text-primary)",
    fontFamily: "inherit"
  };

  const btnQuick = {
    fontSize: 12, padding: "6px 12px", borderRadius: 16,
    border: "0.5px solid var(--color-border-tertiary)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    cursor: "pointer", whiteSpace: "nowrap"
  };

  const btnPrimary = {
    flex: 1, padding: "12px 16px", borderRadius: 6,
    background: valid ? "var(--color-text-primary)" : "var(--color-border-tertiary)",
    color: valid ? "var(--color-background-primary)" : "var(--color-text-secondary)",
    textAlign: "center", textDecoration: "none", fontSize: 14, fontWeight: 500,
    cursor: valid ? "pointer" : "not-allowed",
    pointerEvents: valid ? "auto" : "none", border: "none"
  };

  const btnSecondary = {
    flex: 1, padding: "12px 16px", borderRadius: 6, background: "transparent",
    color: valid ? "var(--color-text-primary)" : "var(--color-text-secondary)",
    textAlign: "center", textDecoration: "none", fontSize: 14, fontWeight: 500,
    border: "0.5px solid " + (valid ? "var(--color-text-primary)" : "var(--color-border-tertiary)"),
    cursor: valid ? "pointer" : "not-allowed", pointerEvents: valid ? "auto" : "none"
  };

  return (
    <div style={{ padding: "1.5rem", maxWidth: 700, margin: "0 auto" }}>
      <h2 style={{ fontSize: 20, fontWeight: 500, margin: "0 0 0.5rem" }}>Bilan comptable</h2>
      <p style={{ fontSize: 14, color: "var(--color-text-secondary)", margin: "0 0 1.5rem" }}>
        Bilan ventes Hiboutik (NF525) à transmettre au comptable. Données certifiées Z mensuels.
      </p>

      <div style={{
        background: "var(--color-background-secondary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: 8, padding: "1.5rem"
      }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Raccourcis
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={setMoisPrecedent} style={btnQuick}>Mois précédent</button>
            {annees.map(y => (
              <button key={y} onClick={() => setAnnee(y)} style={btnQuick}>Année {y}</button>
            ))}
            {bounds && <button onClick={setTout} style={btnQuick}>Tout l'historique</button>}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Du mois</label>
            <input type="month" value={from} onChange={e => setFrom(e.target.value)}
              min={bounds && bounds.from} max={bounds && bounds.to} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "var(--color-text-secondary)", display: "block", marginBottom: 6 }}>Au mois (inclus)</label>
            <input type="month" value={to} onChange={e => setTo(e.target.value)}
              min={bounds && bounds.from} max={bounds && bounds.to} style={inputStyle} />
          </div>
        </div>

        <div style={{
          fontSize: 13, padding: "10px 12px", borderRadius: 6, marginBottom: 16,
          background: "var(--color-background-primary)",
          color: !valid
            ? "var(--color-text-danger)"
            : horsBornes
              ? "var(--color-text-warning)"
              : "var(--color-text-primary)"
        }}>
          Période sélectionnée : <strong>{periodeLabel}</strong>
          {horsBornes && valid && (
            <div style={{ fontSize: 11, marginTop: 4, fontStyle: "italic" }}>
              ⚠️ Période hors plage de données — certains mois seront vides
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <a href={valid ? pdfUrl : "#"} style={btnPrimary}>Télécharger le PDF</a>
          <a href={valid ? xlsxUrl : "#"} style={btnSecondary}>Télécharger l'Excel</a>
        </div>

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "0.5px solid var(--color-border-tertiary)" }}>
          <button
            onClick={handleImport}
            disabled={importing}
            style={{
              width: "100%", padding: "10px 16px", borderRadius: 6,
              border: "0.5px solid var(--color-text-primary)",
              background: importing ? "var(--color-background-secondary)" : "transparent",
              color: "var(--color-text-primary)", fontSize: 13, fontWeight: 500,
              cursor: importing ? "wait" : "pointer", fontFamily: "inherit"
            }}>
            {importing ? "Import en cours..." : "Importer le mois precedent depuis Hiboutik"}
          </button>
          {importMsg && (
            <div style={{
              marginTop: 8, fontSize: 12, padding: "8px 10px", borderRadius: 4,
              background: importMsg.type === "ok" ? "var(--color-background-primary)" : "var(--color-background-primary)",
              color: importMsg.type === "ok" ? "var(--color-text-primary)" : "var(--color-text-danger)",
              border: "0.5px solid " + (importMsg.type === "ok" ? "var(--color-border-tertiary)" : "var(--color-text-danger)")
            }}>
              {importMsg.text}
            </div>
          )}
        </div>

        {!loading && bounds && (
          <p style={{ fontSize: 11, color: "var(--color-text-secondary)", textAlign: "center", marginTop: 16, fontStyle: "italic" }}>
            Données disponibles : {formatLabel(bounds.from)} → {formatLabel(bounds.to)}
          </p>
        )}
        {!loading && !bounds && (
          <p style={{ fontSize: 11, color: "var(--color-text-warning)", textAlign: "center", marginTop: 16, fontStyle: "italic" }}>
            Impossible de charger les bornes de données
          </p>
        )}
      </div>

      <p style={{
        fontSize: 12, color: "var(--color-text-secondary)",
        textAlign: "center", marginTop: 16, fontStyle: "italic"
      }}>
        Par défaut, le mois précédent — le plus pertinent pour la compta.
      </p>
    </div>
  );
}
